import React, { useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import { isMobile } from 'react-device-detect';

const InstituteTeacherList = () => {
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [filterMessage, setFilterMessage] = useState({ type: '', text: '' });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewTeacher, setViewTeacher] = useState(null);
  const AWS = process.env.REACT_APP_AWS_HOST;

  const API = process.env.REACT_APP_API_URL;
  const Institute_token = sessionStorage.getItem('token');
  const Institute_id = sessionStorage.getItem('instituteId');

  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;
  const LMS_HEADERS = {
    'Authorization': token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };

  const fetchTeachers = async (page = 1) => {
    setIsLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        institute_id: Institute_id,
        per_page: perPage,
        page: page
      });

      if (nameFilter.trim()) {
        params.append('name', nameFilter.trim());
      }
      if (emailFilter.trim()) {
        params.append('email', emailFilter.trim());
      }

      const [teacherRes, programRes] = await Promise.all([
        fetch(`${API}/api/teacherbyinstitute?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${Institute_token}`
          }
        }),
        fetch(`${LMS}/api/institutes/${Institute_id}/programs`, {
          method: 'GET',
          headers: LMS_HEADERS
        })
      ]);

      if (!teacherRes.ok) throw new Error('Failed to fetch teachers');
      if (!programRes.ok) throw new Error('Failed to fetch programs');

      const teacherData = await teacherRes.json();
      const programData = await programRes.json();
      const programMap = programData.data.reduce((acc, p) => {
        acc[p.id] = p.program_title;
        return acc;
      }, {});

      const mapped = teacherData.data.map(t => {
        const programIdsRaw = t.teacher_detail?.program_ids;
        const programNames = (programIdsRaw ? String(programIdsRaw).split(',').map(id => programMap[id.trim()] || 'N/A') : ['N/A']);
        const programName = programNames.join(', ');
        return {
          ...t,
          id: t.id,
          name: `${t.first_name} ${t.last_name}`.trim(),
          email: t.email,
          phone: t.contact_number,
          profile_image: t.profile_image,
          status: t.status === 1 ? 'Active' : 'Inactive',
          program: programName,
          programId: programIdsRaw,
          createddate: t.created_at?.split('T')[0],
          subjects: t.teacher_language?.map(l => l.language).join(', ') || 'N/A',
          teacher_detail: t.teacher_detail,
          teacher_language: t.teacher_language
        };
      });

      setTeachers(mapped);
      setPagination({
        current_page: teacherData.current_page,
        last_page: teacherData.last_page,
        per_page: teacherData.per_page,
        total: teacherData.total,
        from: teacherData.from,
        to: teacherData.to
      });
      setCurrentPage(teacherData.current_page);
      
      if (mapped.length === 0 && (nameFilter || emailFilter)) {
        setFilterMessage({ type: 'warning', text: 'No teachers found matching your search criteria.' });
      } else {
        setFilterMessage({ type: '', text: '' });
      }
    } catch (err) {
      setFilterMessage({ type: 'danger', text: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers(1);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTeachers(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [nameFilter, emailFilter]);

  useEffect(() => {
    (async () => {
      try {
        const bootstrap = await import(
          'bootstrap/dist/js/bootstrap.bundle.min.js'
        );
        const triggers = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltips = [...triggers].map(
          (t) => new bootstrap.Tooltip(t, { boundary: 'window' })
        );
        return () => tooltips.forEach((t) => t.dispose());
      } catch {}
    })();
  }, [teachers]);

  const handleReset = () => {
    setNameFilter('');
    setEmailFilter('');
    setFilterMessage({ type: '', text: '' });
  };

  const handleAddTeacher = () => (window.location.href = '/institute-add-teacher');

  const handleView = (teacher) => {
    setViewTeacher(teacher);
    setShowViewModal(true);
  };
  const handleEdit = (teacher) => (window.location.href = `/institute-edit-teacher/${teacher.id}`);
  const handleDelete = (teacher) => {
    setSelectedTeacher(teacher);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/api/teacher`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Institute_token}`
        },
        body: JSON.stringify({ id: selectedTeacher.id })
      });
      if (!response.ok) throw new Error('Failed to delete teacher');
      setFilterMessage({ type: 'success', text: `Teacher "${selectedTeacher.name}" deleted.` });
      fetchTeachers(currentPage);
    } catch (err) {
      setFilterMessage({ type: 'danger', text: err.message });
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setSelectedTeacher(null);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      fetchTeachers(page);
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

  const statusBadge = (s) => {
    switch (s) {
      case 'Active':    return 'bg-success';
      case 'Inactive':  return 'bg-warning text-dark';
      case 'Completed': return 'bg-info';
      default:          return 'bg-secondary';
    }
  };

  const renderPagination = () => {
    if (!pagination || pagination.last_page <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <nav aria-label="Page navigation">
        <ul className="pagination justify-content-center mb-0">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              Previous
            </button>
          </li>
          {startPage > 1 && (
            <>
              <li className="page-item">
                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
              </li>
              {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
            </>
          )}
          {pages.map(page => (
            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(page)}>{page}</button>
            </li>
          ))}
          {endPage < pagination.last_page && (
            <>
              {endPage < pagination.last_page - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
              <li className="page-item">
                <button className="page-link" onClick={() => handlePageChange(pagination.last_page)}>{pagination.last_page}</button>
              </li>
            </>
          )}
          <li className={`page-item ${currentPage === pagination.last_page ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.last_page}>
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div>
      <div className="col-12">
        <PageHeader
          title="Institute Teachers"
          breadcrumbs={[
            { label: 'Dashboard', path: '/institute-dashboard' },
            { label: 'Teachers', active: true },
          ]}
        />
      </div>

      {/* Alert Messages */}
      {filterMessage.text && (
        <div className="row mb-3">
          <div className="col-12">
            <div className={`alert alert-${filterMessage.type} alert-dismissible fade text-white show`} role="alert">
              <strong>
                {filterMessage.type === 'success' ? '✓ Success!' : filterMessage.type === 'warning' ? '⚠ Warning!' : 'ℹ Info!'}
              </strong>
              {' ' + filterMessage.text}
              <button type="button" className="btn-close" onClick={() => setFilterMessage({ type: '', text: '' })} aria-label="Close" />
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar + Add Button */}
      <div className={isMobile ? 'border-0 mb-4 rounded-2 mt-3' : 'mb-4 rounded-2 p-md-3 border'}>
        {/* Desktop Add Button */}
        <div className="row mb-1 d-none d-md-flex">
          <div className="col-9">
            <label className="form-label text-lg fw-semibold text-dark">Filter Options</label>
          </div>
          <div className="col-3 d-flex justify-content-end">
            <button className="btn btn-primary" onClick={handleAddTeacher} disabled={isLoading}>
              <i className="fas fa-plus me-2" /> Add Teacher
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-md-2">
          <div className="col-12">
            {/* Mobile Action Buttons */}
            <div className="d-md-none">
              <div className="d-grid gap-2">
                <button className="btn btn-primary" onClick={handleAddTeacher} disabled={isLoading}>
                  <i className="fas fa-plus me-2" /> Add New Teacher
                </button>
                <button className="btn btn-outline-primary" onClick={() => setShowMobileFilters(true)}>
                  <i className="fas fa-filter me-2" /> Filters
                  {(nameFilter || emailFilter) && (
                    <span className="badge bg-danger ms-2">
                      {[nameFilter, emailFilter].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Desktop Filters */}
            <div className="d-none d-md-block">
              <div className="card-body">
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-dark">Teacher Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name..."
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-dark">Email</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by email..."
                      value={emailFilter}
                      onChange={(e) => setEmailFilter(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2 d-flex justify-content-start">
                    <button className="btn btn-outline-secondary" onClick={handleReset} disabled={isLoading} title="Reset Filters">
                      <i className="fas fa-sync-alt" /> Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filter Sidebar */}
      {showMobileFilters && (
        <>
          <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-md-none" style={{ zIndex: 1040 }} onClick={() => setShowMobileFilters(false)} />
          <div
            className="position-fixed top-0 end-0 h-100 bg-white shadow-lg d-md-none"
            style={{
              width: '320px',
              zIndex: 1050,
              transform: showMobileFilters ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s ease-in-out',
              overflowY: 'auto',
            }}
          >
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-primary text-white">
              <h5 className="mb-0 text-white">
                <i className="fas fa-filter me-2" /> Filters
              </h5>
              <button className="btn btn-sm btn-outline-light" onClick={() => setShowMobileFilters(false)}>
                <i className="fas fa-times" />
              </button>
            </div>

            {/* Filter Content */}
            <div className="p-3">
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Teacher Name</label>
                <input type="text" className="form-control" placeholder="Search by name..." value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Email</label>
                <input type="text" className="form-control" placeholder="Search by email..." value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} />
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 border-top mt-auto">
              <div className="d-grid gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    handleReset();
                    setShowMobileFilters(false);
                  }}
                  disabled={isLoading}
                >
                  <i className="fas fa-sync-alt me-2" /> Reset All Filters
                </button>
                <button className="btn btn-success" onClick={() => setShowMobileFilters(false)}>
                  <i className="fas fa-check me-2" /> Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* TABLE / CARDS */}
      <div className="row">
        <div className="col-12">
          <div className="card skyblueborder">
            <div className="card-header skyblue-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                <i className="fas fa-chalkboard-teacher me-2" /> Teacher List
              </h5>
              {pagination && (
                <span className="badge bg-light text-dark">
                  Showing {pagination.from}-{pagination.to} of {pagination.total}
                </span>
              )}
            </div>
            <div className="card-body p-0">
              {teachers.length > 0 ? (
                <>
                  {/* Desktop Table */}
                  <div className="table-responsive d-none d-md-block">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="border-0 fw-semibold py-3 px-4">Profile</th>
                          <th className="border-0 fw-semibold py-3 px-4">Teacher Name</th>
                          <th className="border-0 fw-semibold py-3 px-4">Email</th>
                          <th className="border-0 fw-semibold py-3 px-4">Phone</th>
                          <th className="border-0 fw-semibold py-3 px-4">Program</th>
                          <th className="border-0 fw-semibold py-3 px-4">Status</th>
                          <th className="border-0 fw-semibold py-3 px-4">Created Date</th>
                          <th className="border-0 fw-semibold py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teachers.map((teacher) => (
                          <tr key={teacher.id}>
                            <td className="py-3 px-4">
                              <img
                                src={`${AWS}${teacher.profile_image}`}
                                alt={teacher.name}
                                className="rounded-circle"
                                width="40"
                                height="40"
                                style={{ objectFit: 'cover' }}
                              />
                            </td>
                            <td className="py-3 px-4">
                              <div className="fw-semibold text-dark">{teacher.name}</div>
                            </td>
                            <td className="py-3 px-4 text-dark">{teacher.email}</td>
                            <td className="py-3 px-4 text-dark">{teacher.phone}</td>
                            <td className="py-3 px-4 text-dark">{teacher.program}</td>
                            <td className="py-3 px-4">
                              <span className={`badge ${statusBadge(teacher.status)}`}>{teacher.status}</span>
                            </td>
                            <td className="py-3 px-4">{formatDate(teacher.createddate)}</td>
                            <td className="py-3 px-4">
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => handleView(teacher)}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="View Teacher"
                                >
                                  <i className="fas fa-eye" />
                                </button>
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => handleEdit(teacher)}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Edit Teacher"
                                >
                                  <i className="fas fa-edit" />
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDelete(teacher)}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Delete Teacher"
                                >
                                  <i className="fas fa-trash" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="d-md-none p-3">
                    {teachers.map((teacher) => (
                      <div key={teacher.id} className="card mb-3 shadow-sm">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <img
                              src={`${AWS}${teacher.profile_image}`}
                              alt={teacher.name}
                              className="rounded-circle me-3"
                              width="50"
                              height="50"
                              style={{ objectFit: 'cover' }}
                            />
                            <div className="flex-grow-1">
                              <div className="fw-semibold text-dark fs-5">{teacher.name}</div>
                              <span className={`badge ${statusBadge(teacher.status)}`}>{teacher.status}</span>
                            </div>
                          </div>

                          <div className="row mb-2">
                            <div className="col-4 text-muted small">Email</div>
                            <div className="col-8 text-dark small">{teacher.email}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-4 text-muted small">Phone</div>
                            <div className="col-8 text-dark small">{teacher.phone}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-4 text-muted small">Program</div>
                            <div className="col-8 text-dark small">{teacher.program}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-4 text-muted small">Created</div>
                            <div className="col-8 text-dark small">{formatDate(teacher.createddate)}</div>
                          </div>

                          <div className="d-flex gap-2 justify-content-end mt-3">
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => handleView(teacher)}
                              title="View Teacher"
                            >
                              <i className="fas fa-eye" /> View
                            </button>
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleEdit(teacher)}
                              title="Edit Teacher"
                            >
                              <i className="fas fa-edit" /> Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(teacher)}
                              title="Delete Teacher"
                            >
                              <i className="fas fa-trash" /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {pagination && pagination.last_page > 1 && (
                    <div className="card-footer bg-light py-3">
                      {renderPagination()}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-chalkboard-teacher fa-3x text-muted mb-3" />
                  <h6 className="text-muted">No Teachers Found</h6>
                  <p className="text-muted mb-3">
                    {(nameFilter || emailFilter)
                      ? 'No teachers match your current filter criteria.'
                      : 'No teachers have been added yet.'}
                  </p>
                  {(nameFilter || emailFilter) ? (
                    <button className="btn btn-outline-primary me-2" onClick={handleReset}>
                      <i className="fas fa-sync-alt me-1" /> Clear Filters
                    </button>
                  ) : null}
                  <button className="btn btn-primary" onClick={handleAddTeacher}>
                    <i className="fas fa-plus me-1" /> Add Teacher
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && viewTeacher && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary pb-3">
                <h5 className="modal-title text-white">
                  <i className="fas fa-user me-2" /> Teacher Details
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowViewModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-3 text-center">
                    <img
                      src={viewTeacher.profile_image ? `${AWS}${viewTeacher.profile_image}` : 'https://via.placeholder.com/150'}
                      alt="Profile"
                      className="rounded-circle img-thumbnail mb-2"
                      width="150"
                      height="150"
                    />
                    <div>
                      <span className={`badge ${statusBadge(viewTeacher.status)}`}>{viewTeacher.status}</span>
                    </div>
                  </div>
                  <div className="col-md-9">
                    <h4 className="mb-1">{viewTeacher.name}</h4>
                    <p className="mb-1"><strong>Email:</strong> {viewTeacher.email}</p>
                    <p className="mb-1"><strong>Phone:</strong> {viewTeacher.phone}</p>
                    <p className="mb-1"><strong>Program:</strong> {viewTeacher.program}</p>
                    <p className="mb-1"><strong>Subjects:</strong> {viewTeacher.subjects}</p>
                    <p className="mb-1"><strong>Created Date:</strong> {formatDate(viewTeacher.createddate)}</p>
                  </div>
                </div>

                {viewTeacher.teacher_detail && (
                  <>
                    <hr />
                    <h5 className="mb-3">Additional Information</h5>
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-1"><strong>Gender:</strong> {viewTeacher.teacher_detail.gender || 'N/A'}</p>
                        <p className="mb-1"><strong>Date of Birth:</strong> {viewTeacher.teacher_detail.date_of_birth || 'N/A'}</p>
                        <p className="mb-1"><strong>Address:</strong> {viewTeacher.teacher_detail.address1 || 'N/A'}</p>
                        {viewTeacher.teacher_detail.address2 && <p className="mb-1">{viewTeacher.teacher_detail.address2}</p>}
                      </div>
                    </div>
                    {viewTeacher.teacher_detail.profile_summary && (
                      <>
                        <p className="mb-1 mt-3"><strong>Profile Summary:</strong></p>
                        <p className="text-muted">{viewTeacher.teacher_detail.profile_summary}</p>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedTeacher && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger pb-3">
                <h5 className="modal-title text-white">
                  <i className="fas fa-exclamation-triangle me-2" /> Confirm Delete
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body text-center">
                <i className="fas fa-trash-alt fa-3x text-danger mb-3" />
                <p className="fs-5">Are you sure you want to delete this teacher?</p>
                <div className="card bg-light mt-3">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-6 text-start">
                        <strong>Name:</strong> {selectedTeacher.name}
                        <br />
                        <strong>Program:</strong> {selectedTeacher.program}
                      </div>
                      <div className="col-6 text-start">
                        <strong>Email:</strong> {selectedTeacher.email}
                        <br />
                        <strong>Status:</strong> {selectedTeacher.status}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="alert alert-warning mt-3">This action cannot be undone.</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)} disabled={isLoading}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={confirmDelete} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-1" /> Delete Teacher
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstituteTeacherList;